import { toast } from "sonner";

import {
  useMergeProfileItems,
  useRemoveProfileItems,
} from "@/hooks/mutations/useContractingMutations";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import type { ContractingItem } from "@/types/domain";
import { displayedAmount } from "@/lib/format";

function daysSinceContratado(awardat?: string | null): number | null {
  if (!awardat) return null;
  const diff = Date.now() - new Date(awardat).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function isSaved(c: ContractingItem): boolean {
  return (
    c.contractinguser != null &&
    c.contractinguser.contractinguser_status !== null &&
    c.contractinguser.contractinguser_status !== undefined
  );
}

/**
 * Estado y acciones de una licitación del listado. Lo comparten la fila de
 * escritorio y la tarjeta de móvil: las dos pintan lo mismo con otra forma.
 */
export function useContractingRow(c: ContractingItem) {
  const mergeMutation = useMergeProfileItems();
  const removeMutation = useRemoveProfileItems();
  const { activeProfileId } = useActiveProfile();

  const saved = isSaved(c);
  // Guardada, pero el perfil activo matcheó ítems extra que aún no se siguen.
  const untrackedCount = saved ? (c.untracked_profile_items_count ?? 0) : 0;
  const hasUntracked = untrackedCount > 0;
  const stage = c.contractingversion_currentstagenormalized;

  // La estrella va por ítems y acotada al perfil activo:
  // - vacía / badge "+N" → agrega los ítems que matcheó este perfil (crea el seguimiento)
  // - llena (ya están todos los míos) → quita los ítems de este perfil
  function onStarClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (activeProfileId == null) return;
    const vars = {
      contracting_id: c.contracting_id,
      searchprofile_id: activeProfileId,
    };
    if (saved && !hasUntracked) {
      removeMutation.mutate(vars, {
        onError: () => toast.error("No se pudieron quitar los ítems"),
      });
    } else {
      mergeMutation.mutate(vars, {
        onError: () => toast.error("No se pudieron agregar los ítems"),
      });
    }
  }

  return {
    locked: c.isLocked === true,
    currencySymbol: c.currency?.currency_symbol ?? "S/",
    // Qué monto se pinta y si salió de las bases: la fila y la tarjeta lo
    // muestran igual, así que se resuelve acá y no en cada una.
    amount: displayedAmount(c),
    matches: c.matchedInterests ?? [],
    stage,
    daysContratado:
      stage?.toUpperCase() === "CONTRATADO"
        ? daysSinceContratado(c.contratado_awardat)
        : null,
    // Pasada la convocatoria, el registro ya no decide nada.
    registrationEndsAt:
      stage?.toUpperCase() === "CONVOCADO" ? (c.registrationEndsAt ?? null) : null,
    saved,
    untrackedCount,
    hasUntracked,
    starDisabled: mergeMutation.isPending || removeMutation.isPending,
    starTitle: hasUntracked
      ? `Este perfil encontró ${untrackedCount} ítem(s) nuevos. Click para agregarlos a tu seguimiento`
      : saved
        ? "Quitar los ítems de este perfil"
        : "Guardar licitación",
    onStarClick,
  };
}
